import * as React from 'react';

import type { MessageRewriteAction } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { mockRewriteMessage } from 'bundles/enterprise-admin-messaging-v2/hooks/mockRewrite';

type Params = {
  /** Current message body. The first triggerRewrite call snapshots this. */
  message: string;
  /** Called with the rewritten body (and with the snapshot on reset). */
  onMessageChange: (next: string) => void;
};

type Result = {
  /** Which pill is currently applied (or in flight). `null` = no rewrite. */
  activeAction: MessageRewriteAction | null;
  /** True while the rewrite request is in flight. */
  isRewriting: boolean;
  /** True once any rewrite has been applied (Reset original is meaningful). */
  hasSnapshot: boolean;
  /** Kick off a rewrite for the given action. */
  triggerRewrite: (action: MessageRewriteAction) => void;
  /** Restore the snapshot taken before the first rewrite. */
  resetOriginal: () => void;
};

/**
 * Tone-rewrite flow on the Review and send screen.
 *
 * The hook owns:
 *   - `activeAction`     — which pill is currently applied.
 *   - `isRewriting`      — true while waiting for the rewrite to land.
 *   - originalSnapshot   — the message body the FIRST time any pill is
 *                          clicked. Reset original always restores this,
 *                          regardless of how many rewrites happened.
 *   - latestRequestId    — monotonic counter so a slow rewrite that
 *                          finishes after a newer pill click (or after
 *                          Reset) is discarded.
 *
 * The current body is forwarded to `mockRewriteMessage` but the mock
 * ignores it — chaining vs. independent-rewrite semantics will be the real
 * backend's call. The hook stays agnostic.
 */
const useToneRewrite = ({ message, onMessageChange }: Params): Result => {
  const [activeAction, setActiveAction] = React.useState<MessageRewriteAction | null>(null);
  const [isRewriting, setIsRewriting] = React.useState(false);
  const [originalSnapshot, setOriginalSnapshot] = React.useState<string | null>(null);
  const latestRequestId = React.useRef(0);

  const triggerRewrite = React.useCallback(
    (action: MessageRewriteAction) => {
      // Capture this BEFORE setOriginalSnapshot so the catch handler knows
      // whether the snapshot we just took belonged to this attempt (and so
      // it can clean up cleanly on rejection).
      const isFirstRewrite = originalSnapshot === null;
      setOriginalSnapshot((previous) => (previous === null ? message : previous));
      setActiveAction(action);
      setIsRewriting(true);

      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;

      mockRewriteMessage(message, action)
        .then((rewritten) => {
          if (latestRequestId.current !== requestId) {
            return;
          }
          onMessageChange(rewritten);
          setIsRewriting(false);
        })
        .catch(() => {
          // Recover: clear loading + active pill so the user can retry.
          // If this was the first rewrite, also drop the snapshot we just
          // took — it equals the current body, so `Reset original` would
          // be a no-op and the link shouldn't appear.
          if (latestRequestId.current !== requestId) {
            return;
          }
          setIsRewriting(false);
          setActiveAction(null);
          if (isFirstRewrite) {
            setOriginalSnapshot(null);
          }
        });
    },
    [message, originalSnapshot, onMessageChange]
  );

  const resetOriginal = React.useCallback(() => {
    if (originalSnapshot === null) {
      return;
    }
    latestRequestId.current += 1;
    onMessageChange(originalSnapshot);
    setOriginalSnapshot(null);
    setActiveAction(null);
    setIsRewriting(false);
  }, [originalSnapshot, onMessageChange]);

  return {
    activeAction,
    isRewriting,
    hasSnapshot: originalSnapshot !== null,
    triggerRewrite,
    resetOriginal,
  };
};

export default useToneRewrite;
